<template>
  <a-modal v-if="visible" title="Review Violation Outcomes" :visible="visible" width="900px" :destroy-on-close="true"
    @cancel="requestClose">
    <div class="authorizations-container">
      <template v-if="step === 1">
        <div v-if="hasApprovalGate" class="confirm-shift__step">Step 1 of 2</div>
        <p class="confirm-shift__intro">
          Your time adjustment changes which violations apply to this shift. Review what will be approved and what no
          longer applies before saving.
        </p>

        <div class="confirm-shift__outcome-grid">
          <section class="confirm-shift__group confirm-shift__group--approved">
            <div class="confirm-shift__group-title">
              <h4>Will Be Approved</h4>
              <p>These violations will stay on the shift or be added by this adjustment, then approved when you confirm.
              </p>
            </div>
            <div v-for="authorization in willBeApprovedAuthorizations"
              :key="`approved-${authorization.source}-${authorization.key}`" class="confirm-shift__auth-card">
              <div class="confirm-shift__auth-header">
                <div class="confirm-shift__auth-name">{{ authorization.title }}</div>
                <a-tag :color="authorization.source === 'existing' ? '#3b82f6' : '#10b981'">
                  {{ authorization.sourceLabel }}
                </a-tag>
              </div>
              <div class="confirm-shift__auth-description">
                {{ authorization.description }}
              </div>
            </div>
            <div v-if="!willBeApprovedAuthorizations.length" class="confirm-shift__empty">
              No violations will be approved.
            </div>
          </section>

          <section class="confirm-shift__group confirm-shift__group--removed">
            <div class="confirm-shift__group-title">
              <h4>No Longer Applies</h4>
              <p>These violations will be removed because this adjustment fixes the condition that triggered them.</p>
            </div>
            <div v-for="authorization in noLongerAppliesAuthorizations" :key="`removed-${authorization.key}`"
              class="confirm-shift__auth-card confirm-shift__auth-card--removed">
              <div class="confirm-shift__auth-header">
                <div class="confirm-shift__auth-name">{{ authorization.title }}</div>
                <a-tag>No Longer Applies</a-tag>
              </div>
              <div class="confirm-shift__auth-description">
                {{ authorization.description }}
              </div>
            </div>
            <div v-if="!noLongerAppliesAuthorizations.length" class="confirm-shift__empty">
              No violations will be removed.
            </div>
          </section>
        </div>
      </template>

      <div v-else class="confirm-shift__approve-step">
        <div class="confirm-shift__step">Step 2 of 2</div>
        <h4>Confirm Violations</h4>
        <p class="confirm-shift__intro">
          Type <b>approve</b> to confirm the violations in Will Be Approved.
        </p>
        <a-input v-model="approvalText" placeholder="Type approve" />
      </div>
    </div>

    <template slot="footer">
      <div class="flex justify-content-between">
        <a-button v-if="step === 1" @click="requestClose"> Cancel </a-button>
        <a-button v-else @click="step = 1"> Back </a-button>
        <a-button type="primary" :loading="loadingSave"
          :disabled="hasApprovalGate && step === 2 && approvalText !== requiredApprovalText"
          @click="handlePrimaryClick">
          {{ primaryButtonText }}
        </a-button>
      </div>
    </template>
  </a-modal>
</template>

<script>
import { Button, Input, Modal, Tag } from 'ant-design-vue'
import { AUTH_STATUSES } from '@/store/modules/shifts/constants'
import AuthReasonMixin from '../../../authorizations/mixins/AuthReasonMixin'

export default {
  components: {
    'a-button': Button,
    'a-input': Input,
    'a-tag': Tag
  },
  mixins: [AuthReasonMixin],
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    addedAuthorizations: {
      type: Array,
      default: () => []
    },
    removedAuthorizations: {
      type: Array,
      default: () => []
    },
    existingAuthorizations: {
      type: Array,
      default: () => []
    },
    loadingSave: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      step: 1,
      approvalText: '',
      requiredApprovalText: 'approve'
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.clearData()
      }
    }
  },
  computed: {
    willBeApprovedAuthorizations() {
      return [
        ...this.existingAuthorizations.map(authorization => this.getAuthorizationCard(authorization, 'existing')),
        ...this.addedAuthorizations.map(authorization => this.getAuthorizationCard(authorization, 'new'))
      ]
    },
    noLongerAppliesAuthorizations() {
      return this.removedAuthorizations.map(authorization => this.getAuthorizationCard(authorization))
    },
    hasApprovalGate() {
      return this.willBeApprovedAuthorizations.length > 0
    },
    primaryButtonText() {
      if (!this.hasApprovalGate) {
        return 'Save Shift'
      }

      return this.step === 1 ? 'Continue' : 'Approve and Save Shift'
    }
  },
  methods: {
    getAuthorizationKey(authorization) {
      if (!authorization) return undefined
      if (typeof authorization === 'string') return authorization

      return authorization.key ?? authorization.type ?? authorization.name
    },
    getAuthorizationCard(authorization, source) {
      const authorizationKey = this.getAuthorizationKey(authorization)

      return {
        key: authorizationKey,
        source,
        sourceLabel: source === 'existing' ? 'Existing' : 'New From This Adjust',
        title: this.getAuthorizationName(authorizationKey),
        description: this.getAuthorizationDescription(authorizationKey)
      }
    },
    getAuthorizationName(authorizationKey) {
      return (
        this.$store.state.applicationState.extendedShiftAuthTypes.find(item => item.key === authorizationKey)?.name ??
        AUTH_STATUSES[authorizationKey]?.title ??
        authorizationKey
      )
    },
    getAuthorizationDescription(authorizationKey) {
      return this.AUTH_RULES[authorizationKey] ?? 'Unfortunately, explanation is not available'
    },
    handlePrimaryClick() {
      if (this.step === 1 && this.hasApprovalGate) {
        this.step = 2
      } else {
        this.$emit('save')
      }
    },
    requestClose() {
      Modal.confirm({
        title: 'Discard changes?',
        content: 'Your shift edits have not been saved yet.',
        onOk: () => {
          this.$emit('update:visible', false)
          this.clearData()
        },
        onCancel() { }
      })
    },
    clearData() {
      this.step = 1
      this.approvalText = ''
    }
  }
}
</script>

<style scoped>
.authorizations-container {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.confirm-shift__step {
  color: var(--text-color-secondary);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.confirm-shift__intro {
  margin: 0;
}

.confirm-shift__outcome-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.confirm-shift__group {
  border: 1px solid var(--border-color-base);
  border-radius: 6px;
  color: var(--text-color);
  padding: 12px;
}

.confirm-shift__group--approved {
  background: rgba(16, 185, 129, 0.12);
  border-color: rgba(16, 185, 129, 0.45);
}

.confirm-shift__group--removed {
  background: var(--bg-component);
}

.confirm-shift__group-title {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 10px;
}

.confirm-shift__group-title h4 {
  margin: 0;
}

.confirm-shift__group-title p {
  color: var(--text-color-secondary);
  font-size: 12px;
  margin: 0;
}

.confirm-shift__auth-card {
  background: var(--bg-component);
  border: 1px solid var(--border-color-base);
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px;
}

.confirm-shift__auth-card+.confirm-shift__auth-card,
.confirm-shift__auth-card+.confirm-shift__empty,
.confirm-shift__empty+.confirm-shift__auth-card {
  margin-top: 8px;
}

.confirm-shift__auth-card--removed {
  color: var(--text-color-secondary);
}

.confirm-shift__auth-header {
  align-items: flex-start;
  display: flex;
  gap: 8px;
  justify-content: space-between;
}

.confirm-shift__auth-name {
  font-weight: 600;
}

.confirm-shift__auth-description {
  font-size: 12px;
}

.confirm-shift__empty {
  color: var(--text-color-secondary);
  font-size: 12px;
  padding: 10px 0;
}

.confirm-shift__approve-step {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.confirm-shift__approve-step h4 {
  margin: 0;
}

::v-deep .ant-tag {
  background: var(--bg-component);
  color: var(--text-color);
}

@media (max-width: 767px) {
  .confirm-shift__outcome-grid {
    grid-template-columns: 1fr;
  }
}
</style>
